import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, PackageCheck } from 'lucide-react';
import VerkoopBronPicker from './VerkoopBronPicker';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import PhoneInput from './PhoneInput';
import { titleCaseName } from '../lib/casing';
import { toast } from 'sonner';

const REGEL_TYPES = ['Arbeid', 'Onderdeel', 'ExtraKosten', 'Tekst', 'Korting'];
const MARKERINGEN = ['Klant', 'Garantie', 'Intern'];
const MARK_BADGE = { Klant: 'green', Garantie: 'amber', Intern: 'gray' };

export default function BonEditor({ bon, onClose }) {
  const { t } = useT();
  const { klanten, klantAutos: alleAutos, createKlant, createBon, updateBon, rdwLookup, gegevens } = useData();
  const klantAutos = React.useMemo(() => (alleAutos || []).filter(a => !a.gearchiveerd), [alleAutos]);
  const normKt = (kt) => (kt || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const gearchiveerd = React.useMemo(() => new Set((alleAutos || []).filter(a => a.gearchiveerd).map(a => normKt(a.kenteken))), [alleAutos]);
  const [showVerkoop, setShowVerkoop] = useState(false);
  const isNew = !bon || !bon.id;
  const hobby = !!gegevens?.hobbyMode;
  const btwPct = hobby ? 0 : (gegevens?.btwPercentage ?? 21);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    import('../api/carsysApi').then(({ carsysApi }) => carsysApi.getTeam().then(setTeam).catch(() => {}));
  }, []);

  // Slimme suggesties: vaste regelslijst (geen AI), tips verschijnen 1x per document
  const [suggestieRegels, setSuggestieRegels] = useState([]);
  const [hiddenTips, setHiddenTips] = useState(() => new Set());
  const eerderGetoond = React.useMemo(() => new Set(bon?.suggestiesGetoond || []), [bon]);
  const shownNowRef = React.useRef(new Set());
  useEffect(() => {
    import('../api/carsysApi').then(({ carsysApi }) =>
      carsysApi.getSuggesties().then(r => setSuggestieRegels(r.regels || [])).catch(() => {}));
  }, []);
  const [data, setData] = useState(() => bon || {
    type: 'Werkbon', klantId: '', kenteken: '', omschrijving: '',
    datum: new Date().toISOString().slice(0, 10),
    regels: [], status: 'Concept', werkStatus: 'Concept', betaald: 0, openstaand: 0, totaal: 0,
    voorgeschotenDoor: '', voorgeschotenBedrag: 0,
    kortingType: 'vast', kortingWaarde: 0,
  });
  const [quickKlant, setQuickKlant] = useState(null); // {naam, telefoon, email}

  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const updRegel = (i, k, v) => setData(d => ({ ...d, regels: d.regels.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addRegel = (type = 'Arbeid') => setData(d => ({ ...d, regels: [...(d.regels || []), { omschr: '', code: '', aantal: 1, prijs: 0, type, markering: 'Klant' }] }));
  const delRegel = (i) => setData(d => ({ ...d, regels: d.regels.filter((_, idx) => idx !== i) }));

  // Standaard artikelcodes (instelbaar in Gegevens, met defaults)
  const milieuBedrag = parseFloat(gegevens?.milieutoeslagBedrag ?? 7.5) || 0;
  const kleinmatPct = parseFloat(gegevens?.kleinmateriaalPercentage ?? 5) || 0;
  const arbeidSubtotaal = (data.regels || [])
    .filter(r => r.type === 'Arbeid' && (r.markering || 'Klant') === 'Klant')
    .reduce((s, r) => s + (parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0), 0);
  const addStandaardcode = (kind) => {
    if (kind === 'milieu') {
      setData(d => ({ ...d, regels: [...(d.regels || []), { omschr: t('Milieutoeslag', 'Milieutoeslag'), code: 'MILIEU', aantal: 1, prijs: milieuBedrag, type: 'ExtraKosten', markering: 'Klant' }] }));
    } else if (kind === 'kleinmateriaal') {
      const bedrag = +(arbeidSubtotaal * kleinmatPct / 100).toFixed(2);
      setData(d => ({ ...d, regels: [...(d.regels || []), { omschr: `${t('Kleinmateriaal', 'Kleinmateriaal')} (${kleinmatPct}%)`, code: 'KLEINMAT', aantal: 1, prijs: bedrag, type: 'ExtraKosten', markering: 'Klant' }] }));
    }
  };

  // Regels splitsen op markering: alleen 'Klant' (of legacy zonder markering) telt voor het klanttotaal
  const isKlantRegel = (r) => (r.markering || 'Klant') === 'Klant';
  const subtotaal = (data.regels || []).reduce((s, r) => {
    if (!isKlantRegel(r)) return s;
    const lineTotal = (parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0);
    // Korting-regels worden ALTIJD afgetrokken, ongeacht of de gebruiker positief of negatief invult
    return r.type === 'Korting' ? s - Math.abs(lineTotal) : s + lineTotal;
  }, 0);
  const garantieBedrag = (data.regels || [])
    .filter(r => (r.markering || 'Klant') === 'Garantie')
    .reduce((s, r) => s + Math.abs((parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0)), 0);
  const internBedrag = (data.regels || [])
    .filter(r => (r.markering || 'Klant') === 'Intern')
    .reduce((s, r) => s + Math.abs((parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0)), 0);
  const kortingWaarde = parseFloat(data.kortingWaarde) || 0;
  const kortingBedrag = data.kortingType === 'percentage'
    ? +(subtotaal * Math.max(0, Math.min(100, kortingWaarde)) / 100).toFixed(2)
    : +Math.max(0, Math.min(subtotaal, kortingWaarde)).toFixed(2);
  const subtotaalNaKorting = +(subtotaal - kortingBedrag).toFixed(2);
  const btw = +(subtotaalNaKorting * btwPct / 100).toFixed(2);
  const totaal = +(subtotaalNaKorting + btw).toFixed(2);

  const eur = (n) => n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

  // Bepaal welke tips relevant zijn voor de huidige bonregels
  const docTekst = ((data.regels || []).map(r => r.omschr || '').join(' | ') + ' | ' + (data.omschrijving || '')).toLowerCase();
  const activeTips = suggestieRegels.filter(rule => {
    if (eerderGetoond.has(rule.id) || hiddenTips.has(rule.id)) return false;
    const kws = rule.keywords || [];
    if (kws.length === 0 || !kws.every(k => docTekst.includes(String(k).toLowerCase()))) return false;
    // Niet tonen als alle gesuggereerde items al op de bon staan
    const sugg = rule.suggesties || [];
    if (sugg.length > 0 && sugg.every(s => docTekst.includes(String(s).toLowerCase().split(' ')[0]))) return false;
    return true;
  });
  activeTips.forEach(tip => shownNowRef.current.add(tip.id));

  const handleRdw = async () => {
    if (!data.kenteken) { toast.error(t('Vul eerst kenteken in')); return; }
    try {
      const info = await rdwLookup(data.kenteken);
      toast.success(`RDW: ${info.merk} ${info.handelsbenaming}`, { description: `APK: ${info.vervaldatumApk || '-'}` });
      if (!data.omschrijving) upd('omschrijving', `${info.merk} ${info.handelsbenaming}`);
    } catch (e) { toast.error(t('RDW lookup mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleSave = async () => {
    if (!data.klantId) { toast.error(t('Kies een klant')); return; }
    if (isNew && data.kenteken && gearchiveerd.has(normKt(data.kenteken))) { toast.error(t('Dit voertuig is gearchiveerd'), { description: t('Zet het eerst terug bij de klant om er een nieuwe bon voor te maken.') }); return; }
    const payload = {
      ...data,
      subtotaal: +subtotaal.toFixed(2),
      kortingType: data.kortingType || 'vast',
      kortingWaarde: kortingWaarde,
      kortingBedrag,
      subtotaalNaKorting,
      btw,
      totaal,
      garantieBedrag: +garantieBedrag.toFixed(2),
      internBedrag: +internBedrag.toFixed(2),
      betaald: data.betaald || 0,
      openstaand: +(totaal - (data.betaald || 0)).toFixed(2),
      suggestiesGetoond: Array.from(new Set([...(bon?.suggestiesGetoond || []), ...shownNowRef.current])),
    };
    try {
      if (isNew) await createBon(payload);
      else await updateBon(data.id, payload);
      toast.success(t(isNew ? 'Bon aangemaakt' : 'Bon opgeslagen'));
      onClose();
    } catch (e) { toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal large" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{isNew ? t('Nieuwe bon') : `${t('Bon')} ${data.nummer || ''} ${t('bewerken', 'bewerken')}`}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label className="cs-section-title">{t('Type')}</label><select className="cs-input" value={data.type} onChange={e => upd('type', e.target.value)}><option value="Werkbon">{t('Werkbon', 'Werkbon')}</option><option value="Offerte">{t('Offerte', 'Offerte')}</option><option value="Factuur">{t('Factuur', 'Factuur')}</option></select></div>
          <div><label className="cs-section-title">{t('Klant')}</label>
            <select className="cs-input" value={data.klantId} onChange={e => {
              const v = e.target.value;
              if (v === '__new__') { setQuickKlant({ naam: '', telefoon: '', email: '' }); return; }
              upd('klantId', v);
              const k = klanten.find(x => x.id === v);
              if (k && !data.kenteken) {
                // Bij voorkeur het hoofdvoertuig invullen, anders het eerste kenteken.
                const main = klantAutos.find(a => a.klantId === v && a.isHoofd);
                const firstKt = (k.kentekens || []).find(kt => !gearchiveerd.has(normKt(kt))) || (main && main.kenteken);
                const kt = main ? main.kenteken : firstKt;
                if (kt) upd('kenteken', kt);
              }
            }}>
              <option value="">-- {t('Kies klant', 'Kies klant')} --</option>
              {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
              <option disabled>──────────</option>
              <option value="__new__">+ {t('Nieuwe klant aanmaken', 'Nieuwe klant aanmaken')}</option>
            </select>
          </div>
          <div><label className="cs-section-title">{t('Datum')}</label><input type="date" className="cs-input" value={data.datum} onChange={e => upd('datum', e.target.value)} /></div>
          <div>
            <label className="cs-section-title">{t('Kenteken')}</label>
            {(() => {
              const klantKentekens = (alleAutos || [])
                .filter(a => a.klantId === data.klantId && (!a.gearchiveerd || a.kenteken === bon?.kenteken))
                .map(a => a.kenteken)
                .filter(Boolean);
              const hasMultiple = klantKentekens.length > 1;
              if (hasMultiple) {
                return (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <select
                      className="cs-input"
                      value={data.kenteken || ''}
                      onChange={e => upd('kenteken', e.target.value.toUpperCase())}
                      data-testid="bon-kenteken-select"
                    >
                      <option value="">— {t('Kies kenteken', 'Kies kenteken')} —</option>
                      {klantKentekens.map(kt => {
                        const auto = (alleAutos || []).find(a => a.kenteken === kt && a.klantId === data.klantId);
                        const label = auto ? `${kt}${auto.isHoofd ? ' ★' : ''}${auto.merk ? ' — ' + auto.merk : ''}${auto.model ? ' ' + auto.model : ''}` : kt;
                        return <option key={kt} value={kt}>{label}</option>;
                      })}
                    </select>
                    <button className="cs-btn" onClick={handleRdw} title="RDW lookup">RDW</button>
                  </div>
                );
              }
              return (
                <div style={{ display: 'flex', gap: 4 }}>
                  <input
                    className="cs-input"
                    value={data.kenteken}
                    onChange={e => upd('kenteken', e.target.value.toUpperCase())}
                    placeholder="12-AB-34"
                    data-testid="bon-kenteken-input"
                  />
                  <button className="cs-btn" onClick={handleRdw} title="RDW lookup">RDW</button>
                </div>
              );
            })()}
          </div>
          <div style={{ gridColumn: '2 / 4' }}><label className="cs-section-title">{t('Omschrijving')}</label><input className="cs-input" value={data.omschrijving} onChange={e => upd('omschrijving', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Kilometerstand', 'Kilometerstand')}</label><input type="number" className="cs-input" value={data.kilometerstand || ''} onChange={e => upd('kilometerstand', e.target.value)} placeholder={t('Bv. 124500', 'Bv. 124500')} data-testid="bon-kilometerstand" /></div>
          <div><label className="cs-section-title">{t('Monteur', 'Monteur')}</label>
            <select className="cs-input" value={data.monteur || ''} onChange={e => upd('monteur', e.target.value)} data-testid="bon-monteur">
              <option value="">— {t('Niemand', 'Niemand')} —</option>
              {team.map(m => <option key={m.id} value={m.naam || m.email}>{m.naam || m.email}</option>)}
            </select>
          </div>
          <div><label className="cs-section-title">{t('Werkdatum', 'Werkdatum')}</label><input type="date" className="cs-input" value={data.werkdatum || ''} onChange={e => upd('werkdatum', e.target.value)} data-testid="bon-werkdatum" /></div>
          {data.type === 'Factuur' && (
            <div><label className="cs-section-title">{t('Aanbetaling (€)', 'Aanbetaling (€)')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({t('reeds voldaan', 'reeds voldaan')})</span></label><input type="number" step="0.01" className="cs-input" value={data.aanbetaling || ''} onChange={e => upd('aanbetaling', parseFloat(e.target.value) || 0)} placeholder="0,00" data-testid="bon-aanbetaling" /></div>
          )}
        </div>

        <div style={{ marginTop: 12, padding: '8px 10px', background: 'var(--cs-card-alt)', borderRadius: 6, display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 10, alignItems: 'end' }}>
          <div>
            <label className="cs-section-title">{t('Voorgeschoten door', 'Voorgeschoten door')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({t('optioneel — wie heeft onderdelen/kosten betaald', 'optioneel — wie heeft onderdelen/kosten betaald')})</span></label>
            <select className="cs-input" value={data.voorgeschotenDoor || ''} onChange={e => upd('voorgeschotenDoor', e.target.value)} data-testid="bon-voorgeschoten-door">
              <option value="">— {t('Niemand', 'Niemand')} —</option>
              {team.map(m => <option key={m.id} value={m.naam || m.email}>{m.naam || m.email}</option>)}
            </select>
          </div>
          <div>
            <label className="cs-section-title">{t('Voorgeschoten bedrag', 'Bedrag (€)')}</label>
            <input type="number" step="0.01" className="cs-input" value={data.voorgeschotenBedrag || 0} onChange={e => upd('voorgeschotenBedrag', parseFloat(e.target.value) || 0)} placeholder="0,00" data-testid="bon-voorgeschoten-bedrag" />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
            <label className="cs-section-title" style={{ margin: 0 }}>{t('Regels', 'Regels')}</label>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {REGEL_TYPES.map(rt => <button key={rt} className="cs-btn ghost" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => addRegel(rt)}><Plus size={11} /> {t(rt, rt)}</button>)}
              <span style={{ width: 1, background: 'var(--cs-border)', margin: '0 2px' }} />
              <button className="cs-btn ghost" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => addStandaardcode('milieu')} data-testid="bon-add-milieu" title={t('Milieutoeslag toevoegen', 'Milieutoeslag toevoegen')}><Plus size={11} /> {t('Milieutoeslag', 'Milieutoeslag')}</button>
              <button className="cs-btn ghost" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => addStandaardcode('kleinmateriaal')} data-testid="bon-add-kleinmateriaal" title={t('Kleinmateriaal toevoegen', 'Kleinmateriaal toevoegen')}><Plus size={11} /> {t('Kleinmateriaal', 'Kleinmateriaal')}</button>
              <span style={{ width: 1, background: 'var(--cs-border)', margin: '0 2px' }} />
              <button className="cs-btn ghost" style={{ padding: '4px 8px', fontSize: 11, color: 'var(--cs-accent)', fontWeight: 600 }} onClick={() => setShowVerkoop(true)} data-testid="bon-add-uit-verkoop" title={t('Verkocht onderdeel, handelauto of voorraad-onderdeel op deze bon zetten')}><PackageCheck size={11} /> {t('Uit verkoop')}</button>
            </div>
          </div>
          <table className="cs-table">
            <thead><tr><th style={{ width: 118 }}>{t('Type')}</th><th style={{ width: 80 }}>{t('Code')}</th><th>{t('Omschrijving')}</th><th style={{ width: 110 }}>{t('Markering', 'Markering')}</th><th style={{ width: 70 }}>{t('Aantal')}</th><th style={{ width: 100 }}>{t('Prijs (€)', 'Prijs (€)')}</th><th style={{ width: 100 }}>{t('Totaal')}</th><th style={{ width: 40 }}></th></tr></thead>
            <tbody>
              {(data.regels || []).map((r, i) => {
                const mark = r.markering || 'Klant';
                const nietGefactureerd = mark !== 'Klant';
                return (
                <tr key={i} style={nietGefactureerd ? { opacity: 0.78 } : undefined}>
                  <td><select className="cs-input" style={{ padding: 4 }} value={r.type} onChange={e => updRegel(i, 'type', e.target.value)}>{(REGEL_TYPES.includes(r.type) ? REGEL_TYPES : [...REGEL_TYPES, r.type]).map(rt => <option key={rt} value={rt}>{t(rt, rt)}</option>)}</select>{(r.bronOnderdeelId || r.bronHandelautoId) && <div style={{ fontSize: 9.5, color: 'var(--cs-accent)', marginTop: 2 }} data-testid={`bon-regel-bron-${i}`}>{t('gekoppeld')}</div>}</td>
                  <td><input className="cs-input" style={{ padding: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={r.code || ''} onChange={e => updRegel(i, 'code', e.target.value.toUpperCase())} placeholder={t('Code', 'Code')} data-testid={`bon-regel-code-${i}`} /></td>
                  <td><input className="cs-input" style={{ padding: 4 }} value={r.omschr} onChange={e => updRegel(i, 'omschr', e.target.value)} placeholder={t('Omschrijving') + '...'} /></td>
                  <td><select className="cs-input" style={{ padding: 4 }} value={mark} onChange={e => updRegel(i, 'markering', e.target.value)} data-testid={`bon-regel-markering-${i}`}>{MARKERINGEN.map(m => <option key={m} value={m}>{t(m, m)}</option>)}</select></td>
                  <td><input type="number" step="0.1" className="cs-input" style={{ padding: 4, textAlign: 'right' }} value={r.aantal} onChange={e => updRegel(i, 'aantal', e.target.value)} /></td>
                  <td><input type="number" step="0.01" className="cs-input" style={{ padding: 4, textAlign: 'right' }} value={r.prijs} onChange={e => updRegel(i, 'prijs', e.target.value)} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: r.type === 'Korting' ? '#dc2626' : nietGefactureerd ? 'var(--cs-text-muted)' : 'inherit' }}>
                    {r.type === 'Korting' ? '− ' : ''}{eur(Math.abs((parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0)))}
                    {nietGefactureerd && <div style={{ fontSize: 10, fontWeight: 400 }}>({t(mark, mark)})</div>}
                  </td>
                  <td><button className="cs-btn ghost danger" onClick={() => delRegel(i)}><Trash2 size={11} /></button></td>
                </tr>
                );
              })}
              {(data.regels || []).length === 0 && <tr><td colSpan={8} className="cs-empty">{t('Voeg regels toe via de knoppen hierboven.', 'Voeg regels toe via de knoppen hierboven.')}</td></tr>}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--cs-card-alt)' }}><td colSpan={6} style={{ textAlign: 'right', padding: 6 }}>{t('Subtotaal')}</td><td style={{ textAlign: 'right', fontWeight: 600, padding: 6 }}>{eur(subtotaal)}</td><td></td></tr>
              <tr style={{ background: 'var(--cs-card-alt)' }}>
                <td colSpan={6} style={{ textAlign: 'right', padding: 6 }}>
                  <span style={{ marginRight: 8 }}>{t('Korting')}</span>
                  <select
                    className="cs-input"
                    style={{ padding: 4, width: 110, display: 'inline-block', marginRight: 6 }}
                    value={data.kortingType || 'vast'}
                    onChange={e => upd('kortingType', e.target.value)}
                    data-testid="bon-korting-type"
                  >
                    <option value="vast">{t('Vast (€)', 'Vast (€)')}</option>
                    <option value="percentage">{t('Percentage (%)', 'Percentage (%)')}</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="cs-input"
                    style={{ padding: 4, width: 90, display: 'inline-block', textAlign: 'right' }}
                    value={data.kortingWaarde || 0}
                    onChange={e => upd('kortingWaarde', e.target.value)}
                    data-testid="bon-korting-waarde"
                    placeholder="0"
                  />
                </td>
                <td style={{ textAlign: 'right', padding: 6, color: kortingBedrag > 0 ? '#dc2626' : 'var(--cs-text-muted)', fontWeight: 600 }} data-testid="bon-korting-bedrag">
                  {kortingBedrag > 0 ? `− ${eur(kortingBedrag)}` : '—'}
                </td>
                <td></td>
              </tr>
              {kortingBedrag > 0 && (
                <tr style={{ background: 'var(--cs-card-alt)' }}><td colSpan={6} style={{ textAlign: 'right', padding: 6, fontSize: 12, color: 'var(--cs-text-muted)' }}>{t('Na korting', 'Na korting')}</td><td style={{ textAlign: 'right', padding: 6, fontSize: 12 }}>{eur(subtotaalNaKorting)}</td><td></td></tr>
              )}
              {hobby ? (
                <tr style={{ background: 'var(--cs-card-alt)' }}><td colSpan={6} style={{ textAlign: 'right', padding: 6, fontSize: 11, color: 'var(--cs-amber)' }}>{t('Hobby modus - geen BTW van toepassing', 'Hobby modus - geen BTW van toepassing')}</td><td style={{ textAlign: 'right', padding: 6, color: 'var(--cs-text-muted)' }}>—</td><td></td></tr>
              ) : (
                <tr style={{ background: 'var(--cs-card-alt)' }}><td colSpan={6} style={{ textAlign: 'right', padding: 6 }}>{t('BTW')} {btwPct}%</td><td style={{ textAlign: 'right', fontWeight: 600, padding: 6 }}>{eur(btw)}</td><td></td></tr>
              )}
              <tr style={{ background: 'var(--cs-accent-soft)' }}><td colSpan={6} style={{ textAlign: 'right', padding: 8, fontSize: 14 }}><b>{t('Totaal')}</b></td><td style={{ textAlign: 'right', fontWeight: 700, padding: 8, fontSize: 14, color: 'var(--cs-accent)' }} data-testid="bon-totaal">{eur(totaal)}</td><td></td></tr>
              {garantieBedrag > 0 && (
                <tr data-testid="bon-garantie-totaal"><td colSpan={6} style={{ textAlign: 'right', padding: 6, fontSize: 12, color: 'var(--cs-amber)' }}>{t('Garantie (niet gefactureerd)', 'Garantie (niet gefactureerd)')}</td><td style={{ textAlign: 'right', padding: 6, fontSize: 12, color: 'var(--cs-amber)' }}>{eur(garantieBedrag)}</td><td></td></tr>
              )}
              {internBedrag > 0 && (
                <tr data-testid="bon-intern-totaal"><td colSpan={6} style={{ textAlign: 'right', padding: 6, fontSize: 12, color: 'var(--cs-text-muted)' }}>{t('Intern (niet gefactureerd)', 'Intern (niet gefactureerd)')}</td><td style={{ textAlign: 'right', padding: 6, fontSize: 12, color: 'var(--cs-text-muted)' }}>{eur(internBedrag)}</td><td></td></tr>
              )}
            </tfoot>
          </table>
          {activeTips.length > 0 && (
            <div data-testid="bon-suggesties" style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {activeTips.map(tip => (
                <div key={tip.id} data-testid={`suggestie-${tip.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: '#92400e' }}>
                  <span style={{ flexShrink: 0 }}>💡</span>
                  <span style={{ flex: 1 }}><b>{t('Vergeet niet', 'Vergeet niet')}:</b> {t(tip.tekst, tip.tekst)}</span>
                  <button data-testid={`suggestie-dismiss-${tip.id}`} onClick={() => setHiddenTips(s => new Set([...s, tip.id]))}
                    title={t('Negeren', 'Negeren')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: 15, lineHeight: 1, padding: '0 2px', fontWeight: 700 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>

      {showVerkoop && <VerkoopBronPicker regels={data.regels} onClose={() => setShowVerkoop(false)} onPick={(regel) => {
        setData(d => ({ ...d, regels: [...(d.regels || []), regel], omschrijving: d.omschrijving || (regel.bronHandelautoId ? `${t('Verkoop voertuig')}: ${regel.omschr}` : `${t('Verkoop onderdeel')}: ${regel.omschr}`) }));
      }} />}
      {quickKlant && (
        <div className="cs-modal-overlay" style={{ zIndex: 200 }} onClick={() => setQuickKlant(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="quick-klant-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{t('Nieuwe klant aanmaken', 'Nieuwe klant aanmaken')}</h3>
              <button className="cs-btn ghost" onClick={() => setQuickKlant(null)}><X size={14} /></button>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label className="cs-section-title">{t('Naam')} *</label>
                <input data-testid="quick-klant-naam" className="cs-input" autoFocus value={quickKlant.naam}
                  onChange={e => setQuickKlant({ ...quickKlant, naam: e.target.value })}
                  onBlur={e => setQuickKlant({ ...quickKlant, naam: titleCaseName(e.target.value) })}
                  placeholder={t('Bv. Jan de Vries', 'Bv. Jan de Vries')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="cs-section-title">{t('Telefoon')}</label>
                  <PhoneInput value={quickKlant.telefoon}
                    onChange={(v) => setQuickKlant({ ...quickKlant, telefoon: v })} testid="quick-klant-tel" />
                </div>
                <div>
                  <label className="cs-section-title">{t('E-mail')}</label>
                  <input className="cs-input" type="email" value={quickKlant.email}
                    onChange={e => setQuickKlant({ ...quickKlant, email: e.target.value })} placeholder="naam@email.nl" />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>
                {t('Tip: vul later adres + voertuig aan via de Klanten pagina.', 'Tip: vul later adres + voertuig aan via de Klanten pagina.')}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setQuickKlant(null)}>{t('Annuleren')}</button>
              <button data-testid="quick-klant-save" className="cs-btn primary" onClick={async () => {
                if (!quickKlant.naam.trim()) { toast.error(t('Naam is verplicht')); return; }
                try {
                  const created = await createKlant({
                    naam: quickKlant.naam.trim(),
                    telefoon: quickKlant.telefoon,
                    email: quickKlant.email,
                    status: 'Actief',
                    laatstGezien: new Date().toISOString().slice(0, 10),
                  });
                  upd('klantId', created.id);
                  setQuickKlant(null);
                  toast.success(t('Klant opgeslagen'));
                } catch (e) {
                  toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
                }
              }}><Save size={14} /> {t('Opslaan')} & {t('Kies klant', 'kiezen').toLowerCase()}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 100; animation: fadeIn 140ms ease; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 560px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); animation: fadeUp 220ms ease; }
        .cs-modal.large { width: 920px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
